import moment from 'moment';

interface IApiBindOption {
  /**
   * APIレスポンス/リクエストの項目名を設定します(必須)
   */
  column: string;  // Default: キャメルケースをスネークケースで解釈させる
  /**
   * toJSONしたときのObjectに含めるかを設定します
   * POSTやPUTする時に不要の場合はfalseを指定してください。
   * Functionを入れるとJSONにする時に変換を行うことができます
   * ex1.) booleanからnumberに変換したい場合
   *   `@ApiBind({ includeJSON: (value) => value === null ? value : Number(value) })`
   *
   * Default: true
   */
  includeJSON?: boolean | ((value) => boolean | number | string);  // Default: true
  /**
   * 項目が`number` or `string`でない時にモデルをnewできる関数をしていしてください。
   * ex1.) 項目の型がDateの場合
   *   `@ApiBind({ model: (value) => new Date(value) })`
   * ex2.) 項目の型がDate Arrayの場合
   *   `@ApiBind({ model: (values) => values.map(value => new Date(value)) })`
   *
   * Default: undefined
   */
  model?: (value) => Date | Common | [];
}

export function ApiBind(option: IApiBindOption): Function {
  return (target: any, propertyName: string) => {
    const binds = target['__ALLOX_BINDS'] || (target['__ALLOX_BINDS'] = {});
    const opt = option;
    if (typeof opt.includeJSON !== 'function') {
      opt.includeJSON = opt.includeJSON === false ? false : true;
    }
    opt.model = opt.model;
    binds[propertyName] = opt;
  };
}

export abstract class Common {

  constructor(response?: {}) {
    this.autoBind(response);
  }

  private autoBind(obj: {}) {
    if (!obj || !this['__ALLOX_BINDS']) { return; }
    const keys = Object.keys(this['__ALLOX_BINDS']);
    for (const key of keys) {
      const opt = this['__ALLOX_BINDS'][key] as IApiBindOption;
      const value = obj[opt.column];
      if (!opt.model) {
        this[key] = value;
      } else if (opt.model) {
        if (value != null) {
          this[key] = opt.model(value);
        } else {
          this[key] = value;
        }
      }
    }
  }

  public toJSON() {
    const result = {};
    const keys = Object.keys(this['__ALLOX_BINDS']);
    for (const key of keys) {
      const opt = this['__ALLOX_BINDS'][key] as IApiBindOption;
      if (!opt.includeJSON) { continue; }
      let value = this[key];
      if (typeof opt.includeJSON === 'function') {
        value = opt.includeJSON(value);
      }
      if (Array.isArray(value)) {
        result[opt.column] = value.map(v => this.convertJSONObject(v));
      } else {
        result[opt.column] = this.convertJSONObject(value);
      }
    }
    return result;
  }

  protected convertJSONObject(obj: any) {
    if (obj == null) {
      return obj;
    }
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return obj;
    }
    if (obj instanceof Common) {
      return obj.toJSON();
    }
    if (obj instanceof Date) {
      return moment(obj).startOf('m').toISOString(true);
    }
  }

  /**
   * APIから取得した値の上書きを行う
   * @param obj APIから取得したモデル
   */
  public overwriteAPIValue(obj: Common) {
    const keys = Object.keys(this['__ALLOX_BINDS']);
    for (const key of keys) {
      if (obj[key] === undefined) { continue; }
      if (obj[key] === this[key]) { continue; }
      this[key] = obj[key];
    }
    return this;
  }
}
